import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { RsvpQuestionType } from '@peles/shared';
import { useDialog } from '../../context/DialogContext';
import {
  useCampaignQuestions,
  useAddCampaignQuestion,
  useUpdateCampaignQuestion,
  useDeleteCampaignQuestion,
} from '../../hooks/useInvites';

const QUESTION_TYPES: { value: RsvpQuestionType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'YES_NO', label: 'Yes / No' },
  { value: 'SINGLE_CHOICE', label: 'Single choice' },
  { value: 'NUMBER', label: 'Number' },
];

/** Custom RSVP questions manager, embedded in the campaign modal (edit mode). */
export function RsvpQuestionsSection({ campaignId }: { campaignId: string }) {
  const { confirm } = useDialog();
  const { data: questions = [] } = useCampaignQuestions(campaignId);
  const addQuestion = useAddCampaignQuestion();
  const updateQuestion = useUpdateCampaignQuestion();
  const deleteQuestion = useDeleteCampaignQuestion();

  const [prompt, setPrompt] = useState('');
  const [questionType, setQuestionType] = useState<RsvpQuestionType>('TEXT');
  const [choices, setChoices] = useState('');

  const handleAdd = async () => {
    if (!prompt.trim()) return;
    await addQuestion.mutateAsync({
      campaignId,
      input: {
        prompt: prompt.trim(),
        questionType,
        sortOrder: questions.length,
        ...(questionType === 'SINGLE_CHOICE' && choices.trim()
          ? {
              config: {
                choices: choices
                  .split(',')
                  .map((c) => c.trim())
                  .filter(Boolean),
              },
            }
          : {}),
      },
    });
    setPrompt('');
    setChoices('');
  };

  const handleDelete = async (questionId: string, questionPrompt: string) => {
    const ok = await confirm({
      message: `Delete question "${questionPrompt}"? Guest answers to it will be removed.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteQuestion.mutateAsync(questionId);
  };

  return (
    <div className="space-y-2" data-testid="rsvp-questions-section">
      <h3 className="text-sm font-semibold text-gray-900">Custom Questions</h3>
      {questions.length === 0 && (
        <p className="text-xs text-gray-500">
          No questions yet — ask about transportation, song requests, anything.
        </p>
      )}
      <ul className="space-y-1.5">
        {questions.map((question) => (
          <li
            key={question.id}
            className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-sm text-gray-900 truncate">{question.prompt}</div>
              <div className="text-xs text-gray-500">
                {QUESTION_TYPES.find((t) => t.value === question.questionType)?.label}
                {question.config?.choices?.length
                  ? ` · ${question.config.choices.join(' / ')}`
                  : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={question.isRequired}
                  onChange={(e) =>
                    void updateQuestion.mutateAsync({
                      questionId: question.id,
                      updates: { isRequired: e.target.checked },
                    })
                  }
                />
                Required
              </label>
              <button
                type="button"
                aria-label={`Delete question ${question.prompt}`}
                onClick={() => void handleDelete(question.id, question.prompt)}
                className="p-1 text-gray-400 hover:text-red-600 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="New question…"
          aria-label="New question prompt"
          className="flex-1 min-w-40 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        />
        <select
          value={questionType}
          onChange={(e) => setQuestionType(e.target.value as RsvpQuestionType)}
          aria-label="Question type"
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {questionType === 'SINGLE_CHOICE' && (
          <input
            type="text"
            value={choices}
            onChange={(e) => setChoices(e.target.value)}
            placeholder="Choices, comma-separated"
            aria-label="Question choices"
            className="flex-1 min-w-40 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        )}
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!prompt.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>
    </div>
  );
}
